/**
 * Stage L cosmetics: the catalog schema shared by client, server, store, progression and admin.
 *
 * A cosmetic item is pure data: an id, a type (which slot it fills), a rarity, short texts, a price
 * hint for the store, how it is obtained and type-specific render parameters. The client renders
 * every type from these parameters (procedural pixel art and sound, no image files), so the admin
 * panel can add items by editing data only. Nothing here ever reaches the simulation: cosmetics
 * are render / audio only and never change the game (no pay-to-win).
 */

/** Every cosmetic type. The order is the order of the collection screen. */
export const COSMETIC_TYPES = [
  "border",
  "fill",
  "colorSet",
  "expansion",
  "conquest",
  "capital",
  "nuke",
  "trail",
  "emote",
  "soundPack",
  "music",
  "victory",
  "nameColor",
  "title",
  "card",
  "flag",
] as const;
export type CosmeticType = (typeof COSMETIC_TYPES)[number];

export const RARITIES = ["common", "rare", "epic", "legendary"] as const;
export type Rarity = (typeof RARITIES)[number];

/** How an item is obtained. "default" items are owned by every account. */
export const COSMETIC_SOURCES = ["default", "store", "level", "pass", "achievement", "supporter", "challenge", "custom"] as const;
export type CosmeticSource = (typeof COSMETIC_SOURCES)[number];

/** "#rrggbb" (lower case). */
export type Hex = string;

export interface LocalText {
  en: string;
  nl: string;
}

/**
 * Price hint for the store (the store's admin config decides the real price). Coins are earned by
 * playing; Crystals are bought. null on the item = not sold (earned through levels, pass, ...).
 */
export interface PriceHint {
  coins?: number;
  crystals?: number;
}

// ---------------------------------------------------------------------------
// Type-specific parameters
// ---------------------------------------------------------------------------

/** Border patterns: drawn on the tile grid along the owner's side of the border. */
export const BORDER_PATTERNS = ["stitch", "beads", "chain", "zigzag", "brick", "rope", "double", "studs"] as const;
export type BorderPattern = (typeof BORDER_PATTERNS)[number];
export interface BorderParams {
  pattern: BorderPattern;
  /** Accent colour mixed into the pattern pixels (the owner colour stays dominant). */
  accent: Hex;
}

/** Territory fill textures: a darker / lighter shade on some tiles of the fill (tile grid). */
export const FILL_TEXTURES = ["checker", "stripes", "dots", "waves", "scales", "cross", "bricks", "stars", "diamonds", "grain"] as const;
export type FillTexture = (typeof FILL_TEXTURES)[number];
export interface FillParams {
  texture: FillTexture;
  /** 1 = faint, 2 = clear, 3 = strong (still capped so borders and names stay readable). */
  strength: 1 | 2 | 3;
}

/** A custom or pre-made colour set: replaces the player's colours on every screen (render only). */
export interface ColorSetParams {
  territory: Hex;
  border: Hex;
  /** Optional border pattern item id worn with the set ("" = keep the equipped border). */
  pattern?: string;
}

/** Particle shapes for expansion and conquest effects (procedural pixel sprites). */
export const PARTICLE_SHAPES = ["spark", "leaf", "star", "ember", "snow", "coin", "bubble", "petal"] as const;
export type ParticleShape = (typeof PARTICLE_SHAPES)[number];
export interface ParticleParams {
  shape: ParticleShape;
  /** 1-3 colours, picked per particle. */
  colors: Hex[];
}

export const CAPITAL_STYLES = ["keep", "tower", "dome", "pagoda", "citadel", "obelisk", "windmill", "lighthouse"] as const;
export type CapitalStyle = (typeof CAPITAL_STYLES)[number];
export interface CapitalParams {
  style: CapitalStyle;
  banner: Hex;
  roof: Hex;
}

export const NUKE_STYLES = ["classic", "ring", "flower", "cube", "star", "spiral"] as const;
export type NukeStyle = (typeof NUKE_STYLES)[number];
export interface NukeParams {
  style: NukeStyle;
  /** [core, middle, edge] colours of the blast rings. */
  colors: [Hex, Hex, Hex];
}

export const TRAIL_STYLES = ["foam", "sparkle", "bubbles", "rainbow", "ember", "ink", "petals", "stars"] as const;
export type TrailStyle = (typeof TRAIL_STYLES)[number];
export interface TrailParams {
  style: TrailStyle;
  colors: Hex[];
  /** Trail length in tiles (3-8). */
  length: number;
}

/** Pixel emotes (8x8 procedural glyphs). `code` is the stable wire number (0-63). */
export const EMOTE_GLYPHS = ["gg", "wave", "laugh", "heart", "crown", "sweat", "zzz", "fist", "flag", "star", "skull", "shield"] as const;
export type EmoteGlyph = (typeof EMOTE_GLYPHS)[number];
export interface EmoteParams {
  glyph: EmoteGlyph;
  colors: [Hex, Hex];
  /** Stable wire number 0..63 (sent as emoji index COSMETIC_EMOTE_BASE + code). */
  code: number;
}

/** Sound packs change the timbre / pitch of YOUR OWN game sounds (only you hear them). */
export const SOUND_VOICES = ["classic", "chip", "brass", "wood", "glass", "drum"] as const;
export type SoundVoice = (typeof SOUND_VOICES)[number];
export interface SoundPackParams {
  voice: SoundVoice;
  /** Semitones -6..6. */
  pitch: number;
}

/** Music themes change the procedural score you hear (only you). */
export const MUSIC_THEMES = ["classic", "heroic", "mystic", "sea", "desert", "frost"] as const;
export type MusicTheme = (typeof MUSIC_THEMES)[number];
export interface MusicParams {
  theme: MusicTheme;
}

export const VICTORY_ANIMATIONS = ["fireworks", "confetti", "crown", "rays", "banner", "stars"] as const;
export type VictoryAnimation = (typeof VICTORY_ANIMATIONS)[number];
export const VICTORY_SOUNDS = ["fanfare", "chime", "drums", "bells"] as const;
export type VictorySound = (typeof VICTORY_SOUNDS)[number];
export interface VictoryParams {
  animation: VictoryAnimation;
  sound: VictorySound;
  colors: Hex[];
  /** Short custom text (custom victory moment only; word-filtered, at most 40 characters). */
  text?: string;
}

export interface NameColorParams {
  color: Hex;
  outline: Hex;
}

export interface TitleParams {
  text: LocalText;
}

export const CARD_BACKGROUNDS = ["plain", "stripes", "grid", "sunburst", "waves", "mountains", "checker", "stars"] as const;
export type CardBackground = (typeof CARD_BACKGROUNDS)[number];
export const CARD_FRAMES = ["none", "thin", "thick", "pixel", "ornate"] as const;
export type CardFrame = (typeof CARD_FRAMES)[number];
export interface CardParams {
  background: CardBackground;
  colors: [Hex, Hex];
  frame: CardFrame;
  /** 0-3 badge glyphs. */
  badges: EmoteGlyph[];
  textColor: Hex;
  /** Short custom line (custom card only; word-filtered, at most 24 characters). */
  text?: string;
}

/** Pixel flag: 16x11 pixels, each a palette index. */
export const FLAG_W = 16;
export const FLAG_H = 11;
export const FLAG_MAX_COLORS = 8;
export interface FlagParams {
  /** FLAG_W * FLAG_H characters "0".."7", row-major, index into `palette`. */
  pixels: string;
  /** 2-8 colours. */
  palette: Hex[];
}

export interface CosmeticParamsByType {
  border: BorderParams;
  fill: FillParams;
  colorSet: ColorSetParams;
  expansion: ParticleParams;
  conquest: ParticleParams;
  capital: CapitalParams;
  nuke: NukeParams;
  trail: TrailParams;
  emote: EmoteParams;
  soundPack: SoundPackParams;
  music: MusicParams;
  victory: VictoryParams;
  nameColor: NameColorParams;
  title: TitleParams;
  card: CardParams;
  flag: FlagParams;
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

/** How the store / admin preview shows an item (derived from the type; see previewKindOf). */
export type PreviewKind = "map" | "particles" | "sprite" | "sound" | "text" | "card" | "flag";

export interface CosmeticItemOf<T extends CosmeticType> {
  /** Stable id: lower-case [a-z0-9.-], 3-48 characters, e.g. "border.stitch.gold". */
  id: string;
  type: T;
  rarity: Rarity;
  /** Display name (at most 32 characters per language). */
  name: LocalText;
  /** One short sentence (at most 90 characters per language). */
  desc: LocalText;
  /** Store price hint; null = not sold (earned). */
  price: PriceHint | null;
  source: CosmeticSource;
  params: CosmeticParamsByType[T];
  /** Optional set id (bundles group items of one set). */
  set?: string;
  /** True for the paid custom creations: the owner edits the params in an editor. */
  editable?: boolean;
  /** Hidden from the collection until owned (e.g. secret achievement rewards). */
  hidden?: boolean;
}

export type CosmeticItem = { [T in CosmeticType]: CosmeticItemOf<T> }[CosmeticType];

/** Item ids every account owns (the defaults). */
export const DEFAULT_ITEM_IDS: readonly string[] = ["title.recruit", "card.basic", "music.classic", "sound.classic"];

// ---------------------------------------------------------------------------
// Loadout (what a player wears) and the wire form that travels with the lobby
// ---------------------------------------------------------------------------

/** Slots that hold one item each (every type except emotes). */
export type SingleSlot = Exclude<CosmeticType, "emote">;
export const SINGLE_SLOTS: readonly SingleSlot[] = COSMETIC_TYPES.filter((t): t is SingleSlot => t !== "emote");
/** A player equips up to this many emotes. */
export const MAX_EMOTES = 4;

/** Custom creations (only valid when the matching editable item is equipped). */
export interface CustomCreations {
  card?: CardParams;
  flag?: FlagParams;
  victory?: VictoryParams;
  colorSet?: ColorSetParams;
}
export const CUSTOM_CREATION_TYPES = ["card", "flag", "victory", "colorSet"] as const;
export type CustomCreationType = (typeof CUSTOM_CREATION_TYPES)[number];
/** Catalog ids of the four paid custom creations (editable items). */
export const CUSTOM_ITEM_IDS: Record<CustomCreationType, string> = {
  card: "custom.card",
  flag: "custom.flag",
  victory: "custom.victory",
  colorSet: "custom.colors",
};

/**
 * A loadout: item id per slot, up to MAX_EMOTES emotes, and the custom creations. This is the shape
 * stored by the accounts loadout API and sent in the lobby (`hello.cosmetics`, `setCosmetics`) and
 * at game start (`gameStart.cosmetics[playerID]`). Always pass it through `sanitizeLoadout`.
 */
export interface Loadout {
  slots: Partial<Record<SingleSlot, string>>;
  emotes: string[];
  custom: CustomCreations;
}

/** Upper bound of a serialized loadout (bytes of JSON) accepted from the network. */
export const MAX_LOADOUT_JSON = 4096;

/** Emoji intent indices from here on are cosmetic emotes (code = index - base). */
export const COSMETIC_EMOTE_BASE = 128;
export const MAX_EMOTE_CODE = 63;

export function emptyLoadout(): Loadout {
  return { slots: {}, emotes: [], custom: {} };
}

export function previewKindOf(type: CosmeticType): PreviewKind {
  switch (type) {
    case "border":
    case "fill":
    case "colorSet":
      return "map";
    case "expansion":
    case "conquest":
    case "trail":
      return "particles";
    case "capital":
    case "nuke":
    case "emote":
      return "sprite";
    case "soundPack":
    case "music":
      return "sound";
    case "victory":
      return "sprite";
    case "nameColor":
    case "title":
      return "text";
    case "card":
      return "card";
    case "flag":
      return "flag";
  }
}
